// Packages and components
import React from 'react';
import { Box, Image } from 'grommet';
import YoutubeButton from '@helpers/YoutubeButton';
import FacebookButton from '@helpers/FacebookButton';
import TrackGameCard from './TrackGameCard';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import TeamInfo from './TeamInfo';
import TeamActionButtons from './TeamActionButtons';

// Types and interfaces
import { shd } from '@shd/jslib/models';
import { RouteComponentProps } from 'react-router-dom';

interface TeamPageHeaderProps {
  games: number;
  history: RouteComponentProps['history'];
  isAdmin: boolean;
  isScoringLink: boolean;
  players: number;
  screenSize: string;
  setShowQrModal: (show: boolean) => void;
  team: shd.Team;
  teamData: shd.Team | null;
}

const TeamPageHeader: React.FC<TeamPageHeaderProps> = ({
  games,
  history,
  isAdmin,
  isScoringLink,
  players,
  screenSize,
  setShowQrModal,
  team,
  teamData,
}) => {
  const headerId = 'team-page-header';

  return (
    <Box
      className={`${headerId}`}
      data-testid={`${headerId}`}
      direction="row"
      wrap={true}
    >
      <Box
        className={`${headerId}__top-row`}
        direction="row"
        justify="between"
        pad={{ horizontal: screenSize === 'small' ? '0' : '16px' }}
        width="100%"
      >
        <Box className={`${headerId}__team-info`} direction="column">
          <Box
            className={`${headerId}__team-info--avatar`}
            round="full"
            border={{
              side: 'all',
              color: 'white',
              size: '2px',
            }}
            height="80px"
            width="80px"
            overflow={'hidden'}
            flex={{ shrink: 0 }}
            background={'light-1'}
            margin={{
              top: screenSize === 'small' ? '-48px' : `-60px`,
            }}
          >
            <Image
              fit="contain"
              src={teamData?.image?.urlT150 || 'image_default.jpg'}
            />
          </Box>
          <TeamInfo team={team} />
        </Box>

        <TeamActionButtons
          isAdmin={isAdmin}
          setShowQrModal={setShowQrModal}
          screenSize={screenSize}
          isScoringLink={isScoringLink}
          teamId={team._id}
          teamNameMed={team.nameMed}
        />
      </Box>

      <Box
        align={screenSize === 'large' ? 'center' : 'start'}
        className={`${headerId}__bottom-row`}
        direction={screenSize === 'large' ? 'row' : 'column'}
        gap={screenSize === 'large' ? '0' : '16px'}
        justify="between"
        margin={{ bottom: '16px' }}
        pad={{ horizontal: screenSize === 'small' ? '0' : '16px' }}
        width={screenSize === 'large' ? '100%' : '100%'}
      >
        <Box
          className={`${headerId}__team-metadata--stream-destination-buttons`}
          direction="row"
          gap="small"
          justify="start"
          key="stream-destination-buttons"
          margin={{ top: 'xxsmall' }}
          pad={{ top: '16px' }}
        >
          <YoutubeButton
            isAdmin={isAdmin}
            newLinkingFlow={true}
            teamId={team?._id}
          />
          <FacebookButton
            isAdmin={isAdmin}
            newLinkingFlow={true}
            teamId={team?._id}
          />
        </Box>

        {isAdmin && (
          <>
            {games === null ||
            players === null ||
            (games >= 1 && players >= 8) ? null : (
              <Box width={screenSize === 'small' ? '100%' : 'auto'}>
                <SHDThemeOverride full={false}>
                  <TrackGameCard
                    games={games}
                    history={history}
                    isAdmin={isAdmin}
                    players={players}
                    team={team}
                    screenSize={screenSize}
                  />
                </SHDThemeOverride>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TeamPageHeader;
